import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const FavoriteKind = z.enum(["music", "song", "wisdom", "activation", "book"]);
const Slug = z.string().min(1).max(200);

// ---------- Favorites ----------

export const listFavorites = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("user_favorites")
      .select("id, kind, slug, created_at")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const toggleFavorite = createServerFn({ method: "POST" })
  .inputValidator((input) => z.object({ kind: FavoriteKind, slug: Slug }).parse(input))
  .middleware([requireSupabaseAuth])
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const existing = await supabase
      .from("user_favorites")
      .select("id")
      .eq("user_id", userId)
      .eq("kind", data.kind)
      .eq("slug", data.slug)
      .maybeSingle();
    if (existing.error) throw new Error(existing.error.message);
    if (existing.data) {
      const { error } = await supabase.from("user_favorites").delete().eq("id", existing.data.id);
      if (error) throw new Error(error.message);
      return { favorited: false };
    }
    const { error } = await supabase
      .from("user_favorites")
      .insert({ user_id: userId, kind: data.kind, slug: data.slug });
    if (error) throw new Error(error.message);
    return { favorited: true };
  });

// ---------- Activation completions ----------

export const listCompletions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("activation_completions")
      .select("id, activation_slug, completed_at")
      .order("completed_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const recordCompletion = createServerFn({ method: "POST" })
  .inputValidator((input) => z.object({ activation_slug: Slug }).parse(input))
  .middleware([requireSupabaseAuth])
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("activation_completions")
      .insert({ user_id: context.userId, activation_slug: data.activation_slug });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------- Listening history ----------

export const recordListening = createServerFn({ method: "POST" })
  .inputValidator((input) =>
    z
      .object({
        release_slug: Slug,
        song_slug: z.string().min(1).max(200).optional().nullable(),
      })
      .parse(input),
  )
  .middleware([requireSupabaseAuth])
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("listening_history").insert({
      user_id: context.userId,
      release_slug: data.release_slug,
      song_slug: data.song_slug ?? null,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const listRecentListening = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("listening_history")
      .select("id, release_slug, song_slug, played_at")
      .order("played_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);
    return data ?? [];
  });
